import { actions, ActorRefFrom, assign, createMachine, InterpreterFrom, raise, send, StateMachine } from "xstate"
import { useActor } from "@xstate/react"
import * as O from "fp-ts/Option"
import { assertEvent } from "../../../_common/xstate-helpers"
import { suiteCategories, SuiteWithPriceAndConfiguration } from "../read-models/suite-category"
import { SanitySuite } from "../../../_common/sanity/sanity-types"
import { Ev } from "../../../_common/machines/event"
import { ECommerceEvent, ECommerceState } from "./ecommerce-machine/e-commerce-machine"

type InitialSuiteCategoriesContext = {
    _selectedSuiteCategoryCode?: string
    suitesSanity: readonly SanitySuite[]
}

type PresentContext = InitialSuiteCategoriesContext & {
    suiteCategories: readonly SuiteWithPriceAndConfiguration<SanitySuite>[]
}

type PresentSuiteSelectedContext = PresentContext & {
    selectedSuiteCategory: SuiteWithPriceAndConfiguration<SanitySuite>
}

type SuiteCategoriesTypeState =
    | { value: "empty"; context: InitialSuiteCategoriesContext }
    | { value: "loading"; context: InitialSuiteCategoriesContext }
    | { value: "loaded"; context: PresentContext }
    | { value: "suiteSelected"; context: PresentSuiteSelectedContext }

type SuiteCategoriesContext = InitialSuiteCategoriesContext &
    Partial<PresentContext> &
    Partial<PresentSuiteSelectedContext>
type SuiteCategoriesEvents =
    | ECommerceEvent
    | Ev<"SELECTED_SUITE_CATEGORY_AVAILABLE", { selectedSuiteCategory: SuiteWithPriceAndConfiguration<SanitySuite> }>
    | Ev<"SELECTED_SUITE_CATEGORY_UNAVAILABLE">

type SuiteCategoriesStateMachine = StateMachine<
    SuiteCategoriesContext,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    SuiteCategoriesEvents,
    SuiteCategoriesTypeState
>
type SuiteCategoriesInterpreter = InterpreterFrom<SuiteCategoriesStateMachine>
export type SuiteCategoriesState = SuiteCategoriesInterpreter["state"]
type SuiteCategoriesSend = SuiteCategoriesInterpreter["send"]
export type SuiteCategoriesActorRef = ActorRefFrom<SuiteCategoriesStateMachine>
type SuiteCategoriesTypes = {
    Context: SuiteCategoriesContext
    Events: SuiteCategoriesEvents
    TypeState: SuiteCategoriesTypeState
    StateMachine: SuiteCategoriesStateMachine
    Interpreter: SuiteCategoriesInterpreter
    State: SuiteCategoriesState
    Send: SuiteCategoriesSend
    ActorRef: SuiteCategoriesActorRef
}

export const suiteCategoriesMachine = (suitesSanity: readonly SanitySuite[]): SuiteCategoriesStateMachine => {
    type Context = SuiteCategoriesTypes["Context"]
    type Events = SuiteCategoriesTypes["Events"]
    type TypeState = SuiteCategoriesTypes["TypeState"]

    return createMachine<Context, Events, TypeState>(
        {
            id: "suite-categories",
            initial: "empty",
            predictableActionArguments: true,
            context: { suitesSanity },
            states: {
                empty: {
                    entry: ["clearAll"],
                },
                loading: {
                    on: {
                        SUITE_PRICES_LOADED: { target: "loaded", actions: ["updateSuiteCategories"] },
                    },
                },
                loaded: {
                    entry: ["syncSelectedSuiteCategory"],
                    on: {
                        SELECTED_SUITE_CATEGORY_AVAILABLE: "suiteSelected",
                    },
                },
                suiteSelected: {
                    on: {
                        SUITE_CATEGORY_UNSELECTED: { target: "loaded", actions: ["clearSelectedSuiteCategory"] },
                        SELECTED_SUITE_CATEGORY_UNAVAILABLE: "loaded",
                    },
                },
            },
            on: {
                SUITE_PRICES_LOADING: "#suite-categories.loading",
                SUITE_PRICES_LOADED: { target: "#suite-categories.loaded", actions: ["updateSuiteCategories"] },
                SUITE_CATEGORY_UNSELECTED: { actions: ["clearSelectedSuiteCategory"] },
                SUITE_CATEGORY_SELECTED: { actions: ["setSelectedSuiteCategory", "syncSelectedSuiteCategory"] },
                QUOTE_SUITE_CATEGORY_SELECTED: { actions: ["setSelectedSuiteCategory", "syncSelectedSuiteCategory"] },
            },
        },
        {
            actions: {
                clearAll: assign<Context, Events>(() => ({
                    suiteCategories: <SuiteWithPriceAndConfiguration<SanitySuite>[]>[],
                    suitesSanity,
                    selectedSuiteCategory: undefined,
                    _selectedSuiteCategoryCode: undefined,
                })),

                updateSuiteCategories: assign<Context, Events>((ctx, ev) => {
                    assertEvent(ev, "SUITE_PRICES_LOADED")
                    return {
                        suiteCategories: suiteCategories(ctx.suitesSanity, ev.prices),
                    }
                }),

                syncSelectedSuiteCategory: actions.pure<Context, Events, Events>((ctx) => {
                    if (!ctx._selectedSuiteCategoryCode || !ctx.suiteCategories) return []

                    const selectedSuiteCategory = ctx.suiteCategories.find(
                        (suiteCategory) => suiteCategory.price.suiteCategory === ctx._selectedSuiteCategoryCode,
                    )

                    if (!selectedSuiteCategory)
                        return [
                            assign<Context, Events>({
                                selectedSuiteCategory: undefined,
                                _selectedSuiteCategoryCode: undefined,
                            }),
                            send<Context, Events>({ type: "SELECTED_SUITE_CATEGORY_UNAVAILABLE" }),
                        ]

                    return [
                        assign<Context, Events>({ selectedSuiteCategory }),
                        raise<Context, Events>({ type: "SELECTED_SUITE_CATEGORY_AVAILABLE", selectedSuiteCategory }),
                    ]
                }),

                setSelectedSuiteCategory: assign<Context, Events>((ctx, ev) => {
                    assertEvent(ev, "SUITE_CATEGORY_SELECTED", "QUOTE_SUITE_CATEGORY_SELECTED")
                    return { _selectedSuiteCategoryCode: ev.suiteCategory }
                }),

                clearSelectedSuiteCategory: assign<Context, Events>({
                    selectedSuiteCategory: undefined,
                    _selectedSuiteCategoryCode: undefined,
                }),

                logMe: actions.log<Context, Events>((ctx, ev) => ({ type: ev.type, ctx, ev })),
            },
        },
    )
}

// eslint-disable-next-line import/no-unused-modules
export const useSuiteCategoriesReadModel = (state: ECommerceState): [SuiteCategoriesState, SuiteCategoriesSend] => {
    const suiteCategoriesMachine = state.children.suiteCategoriesReadModel as SuiteCategoriesActorRef
    return useActor(suiteCategoriesMachine) as unknown as [SuiteCategoriesState, SuiteCategoriesSend]
}

export const isLoaded = (
    state: SuiteCategoriesState,
): state is SuiteCategoriesState & { context: PresentSuiteSelectedContext | PresentContext } =>
    state.matches("loaded") || state.matches("suiteSelected")

export const isSuiteSelected = (
    state: SuiteCategoriesState,
): state is SuiteCategoriesState & { context: PresentSuiteSelectedContext } => state.matches("suiteSelected")

export const withoutAvailability = (state: SuiteCategoriesState): boolean =>
    isLoaded(state) && state.context.suiteCategories.length === 0

export const maybeSelectedSuiteCategory = (
    state: SuiteCategoriesState,
): O.Option<SuiteWithPriceAndConfiguration<SanitySuite>> => O.fromNullable(state.context.selectedSuiteCategory)
